import React, { useEffect, useState } from "react";
import {
  Typography,
  Button,
  TextField,
  Autocomplete,
  Box,
  Checkbox,
} from "@mui/material";
import ConfigWrapper from "../salescodeStudio/mandatoryConfigs/optionalFeatures/widget/ConfigWrapper";
import { Loader } from "../../components/loader/Loader";
import { v4 as uuidv4 } from "uuid";
import {
  ChannelkartNetworkGet,
  ChannelkartNetworkPost,
  META_DATA_BATCH_API,
} from "../../services/networkService";
import { validateWithOtp } from "../../stateManagement/actions/validateOtpPopupActions";
import { manageUpdateAccessObj } from "../manageUpdateAccess/manageUpdateAccessTypes";
import { openPopup } from "../../stateManagement/actions/popupActions";
import { updateConfigRequestBody } from "../../services/managePoliciesService";
import { metaDataBatchPayload } from "../../types/types";
import { getLob } from "../../services/authenticationService";
import { getMetaDataConfig } from "../../services/uiConfigService";
import { getConfigFromClientConfig } from "../../services/bannerServices";
import { ConfirmationPopUp } from "../../components/popup/confirmationPopUp";

const ClaimsAmt = () => {
  const [loading, setLoading] = useState(true);
  const [divisionOptions, setDivisionOptions] = useState<string[]>([]);
  const [distributorOptions, setDistributorOptions] = useState<string[]>([]);
  const [allDistributors, setAllDistributors] = useState<any[]>([]);
  const [selectedDivisions, setSelectedDivisions] = useState<string[]>([]);
  const [selectedDistributors, setSelectedDistributors] = useState<string[]>([]);
  const [rollingMonth, setRollingMonth] = useState("");
  const [percentage, setPercentage] = useState("");
  const [pendingDivisions, setPendingDivisions] = useState<string[]>([]);
  const [confirmationPopUp, setConfirmationPopUp] = useState(false);

  const [errors, setErrors] = useState({
    division: "",
    distributor: "",
    rollingMonth: "",
    percentage: "",
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const divisionRes = await ChannelkartNetworkGet(
          "/v1/productAuthCodeMapping?size=10000"
        );
        const divisions = divisionRes.data.features;

        const uniqueProdAuthCodes = Array.from(
          new Set(
            (divisions as any[])
              .map((item) => item?.prodAuthCode)
              .filter((code): code is string => typeof code === "string")
          )
        );
        setDivisionOptions(uniqueProdAuthCodes);

        const distributorRes = await ChannelkartNetworkGet(
          "/users/mapped/hierarchy?filter=activeStatus%3Aactive%20and%20designation%5Bin%5D%3A%5Bsupplier%5D&size=10000"
        );
        const distributors = distributorRes.data.features;
        setAllDistributors(distributors); 
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedDivisions.length === 0) {
      setDistributorOptions([]);
      return;
    }

    const filtered = allDistributors
      .filter((dist) => {
        if (!dist.prodauthcode) return false;
        const codes = dist.prodauthcode.split(",").map((c:any) => c.trim());
        return codes.some((code:any) => selectedDivisions.includes(code));
      })
      .map((dist) => dist.loginId);

    const uniqueLoginIds = Array.from(new Set(filtered));
    setDistributorOptions(uniqueLoginIds);
    setSelectedDistributors([]);
  }, [selectedDivisions, allDistributors]);

  const validateForm = () => {
    let hasError = false;
    const newErrors = {
      division: "",
      distributor: "",
      rollingMonth: "",
      percentage: "",
    };

    if (selectedDivisions.length === 0) {
      newErrors.division = "Division selection is required.";
      hasError = true;
    }
    if (selectedDistributors.length === 0) {
      newErrors.distributor = "Distributor selection is required.";
      hasError = true;
    }    

    if (Number(rollingMonth) < 1) {
      newErrors.rollingMonth = "Rolling months must be at least 1.";
      hasError = true;
    }

    if (Number(percentage) <= 0) {
      newErrors.percentage = "Percentage must be greater than 0.";
      hasError = true;
    }

    setErrors(newErrors);
    return !hasError;
  };

  const handleSave = async () => {
    try {
      if (!validateForm()) return;
  
      const commonConfig = {
        domainName: "claimValidation",
        domainType: "claimAmount",
        col1key: "claimType",
        col1value: "DAMAGERETURNS",
        col3key: "division",
        col3value: selectedDivisions.join(","),
        col4key: "userList",
        col4value: selectedDistributors.join(","),
        col5key: "aggregationMode",
        col5value: "monthly",
        col7key: "config",
        col7value: {
          rm: Number(rollingMonth),
          p: Number(percentage),
        },
      };
  
      const payload1 = {
        ...commonConfig,
        col2key: "claimSubType",
        col2value: "PURCHASERETURNS",
      };
  
      const payload2 = {
        ...commonConfig,
        col2key: "claimSubType",
        col2value: "SALERETURNS",
      };
  
      const updatedClaimAmountConfig = [payload1, payload2];
  
      let successCount = 0;
      let failureCount = 0;
  
      for (const config of updatedClaimAmountConfig) {
        try {
          const response = await ChannelkartNetworkPost("/v1/metaDataFilter", config);
          if (response.status >= 200 && response.status < 300) {
            successCount++;
          } else {
            failureCount++;
          }
        } catch (err) {
          failureCount++;
        }
      }
  
      const total = successCount + failureCount;
      if (failureCount === 0) {
        openPopup("Success", `Record created successfully.`);
        setSelectedDivisions([]);
        setSelectedDistributors([]);
        setRollingMonth("");
        setPercentage("");
        setErrors({
          division: "",
          distributor: "",
          rollingMonth: "",
          percentage: "",
        });
      } else if (successCount === 0) {
        openPopup("Error", `All ${total} record(s) failed to create.`);
      } else {
        openPopup(
          "Error",
          `${total} record(s) processed: ${successCount} success, ${failureCount} failure.`
        );
      }
    } catch (error) {
      openPopup("Error", "Something went wrong during submission.");
    }
  };
  

  if (loading) {
    return <div className="config-container-left"><Loader /></div>;
  }

  return (
    <div className="config-container-left" style={{ marginTop: "0", padding: "30px" }}>
      <div className="config-heading" style={{ height: "auto", width: "100%" }}>
        <div>
        <Typography
          sx={{
            fontSize: { xs: "1rem", md: "1.2rem", xl: "2.8rem" },
            color: "#227C9D",
          }}
        >
          <span style={{ color: "#00C6B1", fontWeight: "bold" }}>CLAIMS</span> CONFIGURATION
        </Typography>
        <Typography style={{ fontSize: "clamp(0.4rem, 0.8vw + 0.1rem, 1.8rem)" }}>
          The configuration centralizes key details, crucial for the portal’s framework and deployment.
        </Typography>
        </div>
      </div>

      <div className="config-main-logic-container">
        <div className="config-main">
          <ConfigWrapper heading="Claim Eligibility Criteria" customStyles={{ marginTop: "0rem", fontSize: "20px" }}>
            <Box display="flex" flexDirection="column" gap={2}>

              <Box display="flex" flexDirection="column" gap={1}>
                <Typography color="#606060">
                  Please select the division you want to apply claim eligibility for
                  <span style={{ color: "red" }}>*</span>
                </Typography>
                <Autocomplete
                  multiple
                  limitTags={5}
                  disableCloseOnSelect
                  options={divisionOptions.length > 0 ? ["Select All", ...divisionOptions] : []}
                  value={selectedDivisions}
                  onChange={(event, newValue) => {
                    let updatedDivisions =
                      newValue.includes("Select All")
                        ? selectedDivisions.length === divisionOptions.length
                          ? []
                          : divisionOptions
                        : newValue.filter((val) => val !== "Select All");
                  
                    if (selectedDistributors.length > 0) {
                      setPendingDivisions(updatedDivisions);
                      setConfirmationPopUp(true); // show confirmation
                    } else {
                      setSelectedDivisions(updatedDivisions); // no distributors selected, safe to change
                    }
                  }}                  
                  getOptionLabel={(option) => option}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        style={{ marginRight: 8 }}
                        checked={
                          option === "Select All"
                            ? selectedDivisions.length === divisionOptions.length
                            : selectedDivisions.includes(option)
                        }
                      />
                      {option}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Division"
                      size="small"
                      error={Boolean(errors.division)}
                      helperText={errors.division}
                    />
                  )}
                  sx={{ width: "320px" }}
                />
              </Box>

              <Box display="flex" flexDirection="column" gap={1}>
                <Typography color="#606060">
                  Please select the distributor you want to apply claim eligibility for
                  <span style={{ color: "red" }}>*</span>
                </Typography>
                <Autocomplete
                  multiple
                  limitTags={5}
                  disableCloseOnSelect
                  options={distributorOptions.length > 0 ? ["Select All", ...distributorOptions] : []}
                  value={selectedDistributors}
                  onChange={(event, newValue) => {
                    if (newValue.includes("Select All")) {
                      setSelectedDistributors(
                        selectedDistributors.length === distributorOptions.length ? [] : distributorOptions
                      );
                    } else {
                      setSelectedDistributors(newValue.filter((val) => val !== "Select All"));
                    }
                  }}
                  getOptionLabel={(option) => option}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        style={{ marginRight: 8 }}
                        checked={
                          option === "Select All"
                            ? selectedDistributors.length === distributorOptions.length
                            : selectedDistributors.includes(option)
                        }
                      />
                      {option}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select Distributor" size="small" error={Boolean(errors.distributor)} helperText={errors.distributor}/>
                  )}
                  sx={{ width: "320px" }}
                />
              </Box>

              <Box display="flex" flexDirection="column" gap={1}>
                <Typography color="#606060">
                  Set Rolling Months for Primary amount calculation
                  <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  type="number"
                  size="small"
                  value={rollingMonth}
                  onKeyDown={(e) => {
                    if (e.key === "." || e.key === "e" || e.key === "+" || e.key === "-") {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d*$/.test(val)) { 
                      setRollingMonth(val);
                    }
                  }}
                  error={Boolean(errors.rollingMonth)}
                  helperText={errors.rollingMonth}
                  InputProps={{
                    endAdornment: (
                      <span style={{ marginLeft: 4, fontSize: "0.85rem" }}>Months</span>
                    ),
                  }}
                  sx={{ width: "320px" }}
                />
              </Box>

              <Box display="flex" flexDirection="column" gap={1}>
                <Typography color="#606060">
                  Set Percentage(%) of eligible claim based on rolling month's Primary amount
                  <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  type="number"
                  size="small"
                  value={percentage}
                   onKeyDown={(e) => {
                    if (e.key === "e") {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => setPercentage(e.target.value)}
                  error={Boolean(errors.percentage)}
                  helperText={errors.percentage}
                  InputProps={{
                    endAdornment: (
                      <span style={{ marginLeft: 4, fontSize: "0.85rem" }}>%</span>
                    ),
                  }}
                  sx={{ width: "320px" }}
                />
              </Box>
            </Box>
          </ConfigWrapper>
        </div>

        <div className="config-save-button-container">
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              alignSelf: "flex-end",
              marginY: "1rem",
              fontSize: "clamp(0.5rem,0.9vw + 0.065rem, 1.6rem)",
              gap: "7px",
            }}
          >
            SAVE
            <svg stroke="currentColor" fill="currentColor" viewBox="0 0 512 512" height="1.2em" width="1.2em">
              <path d="M502.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-128-128c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L402.7 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l370.7 0-73.4 73.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l128-128z" />
            </svg>
          </Button>
        </div>
      </div>
      <ConfirmationPopUp
        openConfirmModal={confirmationPopUp}
        message="Changing division will clear all selected distributors. Do you want to continue?"
        setOpenConfirmModal={() => setConfirmationPopUp(false)}
        successMethod={() => {
          setSelectedDivisions(pendingDivisions);
          setSelectedDistributors([]);
          setConfirmationPopUp(false);
        }}
      />
    </div>
  );
};

export default ClaimsAmt;
